import { flattenProp } from 'recompose'

import { userSelector } from 'auth/selectors'

import { withRouteParams, loadOnMount } from 'shared/hoc'
import useLocationTimezone from 'shared/hooks/useLocationTimezone'

import { T, withRouter, withTranslatedMeta } from 'i18n/utils'
import { SEARCH_MODE_HOURLY, SEARCH_MODE_MONTHLY, SEARCH_MODE_EVENTS, SEARCH_MODE_PREPAID } from 'search/constants'
import { selectedEventSelector } from 'search/selectors'
import featureToggles from 'shared/featureToggles'
import Modal from 'shared/components/Modal'
import { setGoogleReserveCookiesFromUrl, getRwgToken } from 'analytics/utils'
import {
    getBookingLocationDetails,
    setParkingMode,
    discardThreeDs,
    challenge3ds,
    setCurrentCustomerFlow,
} from 'booking/actions'
import {
    locationDetailsPendingSelector,
    locationDetailsSelector,
    stateSelector,
    rateSelector,
    threeDsDataSelector,
    currentCustomerFlowSelector,
    purchaseCompletedSelector,
} from 'booking/selectors'

import { reservationViaUrlSelector } from 'dashboard-transactions/selectors'
import { pixQrCodeDataSelector } from 'credit-cards/selectors'

import { BookingSelection } from 'booking/components/BookingSelection'
import Secure3dFormModal from 'booking/components/flow-steps/Secure3dFormModal'
import { SHOW_EVENTS_FLOW, SHOW_HOURLY_FLOW_PARK_NOW, SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE } from 'booking/constants'
import 'booking/styles.scss'

const BookingView = ({
    location,
    defaultCustomerFlow,
    parkingFlowUrlParameter,
    detailsPending,
    purchaseCompleted,
    grsId,
    history,
    rate,
    selectedSeasonTicket,
    setParkingMode,
    parkingMode,
    selectedEvent,
    user,
    qrCodeData,
    threeDs,
    discardThreeDs,
    setCurrentCustomerFlow,
    existingReservation,
    currentCustomerFlow,
}) => {
    const [threeDsModalOpen, setThreeDsModalOpen] = React.useState(false)
    const [restrictionsModalOpen, setRestrictionsModalOpen] = React.useState(
        Boolean(location?.dataFields?.restriction1)
    )
    const initialFlowEffectRun = React.useRef(false)

    const getRate = () => {
        switch (parkingMode) {
            case SEARCH_MODE_HOURLY:
                return rate
            case SEARCH_MODE_MONTHLY:
                return selectedSeasonTicket.rate
            case SEARCH_MODE_EVENTS:
                if (featureToggles.hasEventFlow) {
                    return selectedEvent
                }
                break
            default:
                return rate
        }
    }

    const setBookingFlow = () => {
        const hasParkNowFlow = defaultCustomerFlow?.split('|').includes(SHOW_HOURLY_FLOW_PARK_NOW)

        // already in hourly mode, set customer flow (like coming from search page)
        if (parkingMode === SEARCH_MODE_HOURLY && !parkingFlowUrlParameter && !existingReservation) {
            if (hasParkNowFlow) {
                !currentCustomerFlow && setCurrentCustomerFlow(SHOW_HOURLY_FLOW_PARK_NOW)
                return
            }

            setCurrentCustomerFlow(SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE)
            return
        }
        //no search mode is set
        if (!parkingFlowUrlParameter && parkingMode !== SEARCH_MODE_HOURLY && parkingMode !== SEARCH_MODE_PREPAID) {
            setParkingMode(SEARCH_MODE_HOURLY)
            if (hasParkNowFlow && !currentCustomerFlow && !existingReservation) {
                setCurrentCustomerFlow(SHOW_HOURLY_FLOW_PARK_NOW)
            } else {
                setCurrentCustomerFlow(SHOW_HOURLY_FLOW_BOOK_IN_ADVANCE)
            }
        } else if (parkingFlowUrlParameter && parkingMode !== parkingFlowUrlParameter) {
            if (!featureToggles.hasEventFlow && parkingFlowUrlParameter === SEARCH_MODE_EVENTS) {
                return
            }
            setParkingMode(parkingFlowUrlParameter)
            setCurrentCustomerFlow(null)
        }
    }

    useLocationTimezone(location?.timeZone)

    React.useEffect(
        () => () => {
            setCurrentCustomerFlow(null)
        },
        []
    )

    React.useEffect(() => {
        if (grsId && getRwgToken()) {
            setGoogleReserveCookiesFromUrl(grsId)
        }
    }, [grsId])

    React.useEffect(() => {
        if (defaultCustomerFlow && !initialFlowEffectRun.current) {
            initialFlowEffectRun.current = true

            if (!parkingFlowUrlParameter && defaultCustomerFlow === SHOW_EVENTS_FLOW) {
                setParkingMode(SEARCH_MODE_EVENTS)
                history.push(`/booking/${grsId}/events`)
            }
        }
    }, [defaultCustomerFlow, parkingFlowUrlParameter, grsId])

    React.useEffect(() => {
        if (!defaultCustomerFlow || defaultCustomerFlow?.length === 0) return
        setBookingFlow()
    }, [parkingFlowUrlParameter, defaultCustomerFlow])

    React.useEffect(() => {
        if (purchaseCompleted) {
            qrCodeData ? history.push('pix') : history.push('confirmation')
        }
    }, [purchaseCompleted])

    React.useEffect(() => {
        if (threeDs) {
            setThreeDsModalOpen(true)
        } else {
            setThreeDsModalOpen(false)
        }
    }, [threeDs])

    React.useEffect(() => {
        if (Boolean(location?.dataFields?.restriction1)) {
            setRestrictionsModalOpen(true)
        }
    }, [location?.dataFields])

    return (
        <>
            {featureToggles.showBookingRestrictions && restrictionsModalOpen && (
                <Modal onClose={() => setRestrictionsModalOpen(false)} header={<T>booking.restrictions.title</T>}>
                    <div dangerouslySetInnerHTML={{ __html: location.dataFields.restriction1 }} />
                </Modal>
            )}
            {threeDsModalOpen && threeDs && <Secure3dFormModal data={threeDs} onClose={discardThreeDs} />}
            <BookingSelection
                grsId={grsId}
                locationDetails={location}
                detailsPending={detailsPending}
                parkingMode={parkingMode}
                user={user}
                rateVehicle={getRate()}
                defaultCustomerFlow={defaultCustomerFlow}
                currentCustomerFlow={currentCustomerFlow}
                showOnlyBookInAdvanceFlow={existingReservation}
            />
        </>
    )
}

export default compose(
    withTranslatedMeta('booking.title'),
    withRouter,
    withRouteParams,
    connect(
        createStructuredSelector({
            purchaseCompleted: purchaseCompletedSelector,
            detailsPending: locationDetailsPendingSelector,
            location: locationDetailsSelector,
            rate: rateSelector,
            bookingState: stateSelector,
            selectedEvent: selectedEventSelector,
            user: userSelector,
            qrCodeData: pixQrCodeDataSelector,
            threeDs: threeDsDataSelector,
            existingReservation: reservationViaUrlSelector,
            currentCustomerFlow: currentCustomerFlowSelector,
        }),
        {
            getBookingLocationDetails,
            setParkingMode,
            discardThreeDs,
            challenge3ds,
            setCurrentCustomerFlow,
        }
    ),
    loadOnMount('getBookingLocationDetails', ({ grsId }) => [grsId]),
    flattenProp('location'),
    flattenProp('address'),
    flattenProp('bookingState')
)(BookingView)
